import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Edit, Trash2, Users, Check, X } from "lucide-react";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import { PageHeader, PageShell } from "@/components/PageShell";
import { InlineHint, PageEmptyState, PageLoadingState } from "@/components/PageStates";
import { getFieldErrorClass, getFieldMessageClass, isValidEmail } from "@/lib/form-ui";
import { ROLE_ADMIN, ROLE_KEPALA_RUANGAN, ROLE_PENGAWAS, ROLE_USER } from "@/lib/roles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [signupRequests, setSignupRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSignupRequests, setLoadingSignupRequests] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: ROLE_ADMIN,
  });
  const [currentId, setCurrentId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Ya",
    destructive: false,
    onConfirm: null,
  });

  useEffect(() => {
    fetchData();
    fetchSignupRequests();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Gagal memuat data pengguna");
    } finally {
      setLoading(false);
    }
  };

  const fetchSignupRequests = async () => {
    setLoadingSignupRequests(true);
    try {
      const response = await axios.get(`${API}/admin-signup-requests`, {
        params: { status: "Pending" },
      });
      setSignupRequests(response.data);
    } catch (error) {
      toast.error("Gagal memuat permintaan pendaftaran admin");
    } finally {
      setLoadingSignupRequests(false);
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const trimmedUsername = String(formData.username || "").trim();
    const trimmedEmail = String(formData.email || "").trim();

    if (!trimmedUsername) {
      nextErrors.username = "Username wajib diisi.";
    } else if (trimmedUsername.length < 3) {
      nextErrors.username = "Username minimal 3 karakter.";
    }

    if (!trimmedEmail) {
      nextErrors.email = "Email wajib diisi.";
    } else if (!isValidEmail(trimmedEmail)) {
      nextErrors.email = "Format email belum valid.";
    }

    if (!editMode && !String(formData.password || "")) {
      nextErrors.password = "Password wajib diisi untuk pengguna baru.";
    } else if (String(formData.password || "") && String(formData.password).length < 8) {
      nextErrors.password = "Password minimal 8 karakter.";
    }

    if (!String(formData.role || "").trim()) {
      nextErrors.role = "Role wajib dipilih.";
    }

    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateForm();
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setConfirmDialog({
      open: true,
      title: editMode ? "Konfirmasi Perbarui Pengguna" : "Konfirmasi Simpan Pengguna",
      description: editMode
        ? "Apakah Anda yakin ingin memperbarui data pengguna ini?"
        : "Apakah Anda yakin ingin menyimpan data pengguna ini?",
      confirmText: editMode ? "Ya, Perbarui" : "Ya, Simpan",
      destructive: false,
      onConfirm: async () => {
        try {
          if (editMode) {
            const updateData = { ...formData };
            if (!updateData.password) delete updateData.password;
            await axios.put(`${API}/users/${currentId}`, updateData);
            toast.success("Pengguna berhasil diupdate");
          } else {
            await axios.post(`${API}/auth/register`, formData);
            toast.success("Pengguna berhasil ditambahkan");
          }
          setDialogOpen(false);
          resetForm();
          fetchData();
          fetchSignupRequests();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menyimpan data");
        }
      },
    });
  };

  const handleEdit = (item) => {
    setFormErrors({});
    setFormData({
      username: item.username,
      email: item.email,
      password: "",
      role: item.role,
    });
    setCurrentId(item.user_id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: "Konfirmasi Hapus Pengguna",
      description: "Apakah Anda yakin ingin menghapus pengguna ini? Tindakan ini tidak dapat dibatalkan.",
      confirmText: "Ya, Hapus",
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/users/${id}`);
          toast.success("Pengguna berhasil dihapus");
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menghapus pengguna");
        }
      },
    });
  };

  const resetForm = () => {
    setFormErrors({});
    setFormData({
      username: "",
      email: "",
      password: "",
      role: ROLE_ADMIN,
    });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleApproveSignupRequest = (requestId, username) => {
    setConfirmDialog({
      open: true,
      title: "Setujui Pendaftaran Admin",
      description: `Setujui pendaftaran admin untuk akun ${username}?`,
      confirmText: "Ya, Setujui",
      destructive: false,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/admin-signup-requests/${requestId}/approve`);
          toast.success("Pendaftaran admin disetujui");
          fetchData();
          fetchSignupRequests();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menyetujui pendaftaran");
        }
      },
    });
  };

  const handleRejectSignupRequest = (requestId, username) => {
    setConfirmDialog({
      open: true,
      title: "Tolak Pendaftaran Admin",
      description: `Tolak pendaftaran admin untuk akun ${username}? Pengguna bisa mendaftar ulang nanti.`,
      confirmText: "Ya, Tolak",
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/admin-signup-requests/${requestId}/reject`, {
            reason: "Ditolak oleh Kepala Ruangan",
          });
          toast.success("Pendaftaran admin ditolak");
          fetchSignupRequests();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menolak pendaftaran");
        }
      },
    });
  };

  if (loading) {
    return <PageLoadingState label="Memuat manajemen pengguna" description="Menyiapkan daftar akun dan permintaan pendaftaran admin." />;
  }

  return (
    <PageShell data-testid="user-management-page">
      <PageHeader
        eyebrow="Administrasi Akses"
        title="Manajemen Pengguna"
        description="Kelola akun, role, dan permintaan pendaftaran admin dengan struktur yang lebih mudah ditinjau."
        actions={(
          <Button
            data-testid="add-user-button"
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
          >
            <Plus size={16} className="mr-2" />
            Tambah Pengguna
          </Button>
        )}
      />

      <InlineHint>
        Hak akses pengguna tidak berubah. Perbaikan pada fase ini hanya merapikan struktur halaman dan kejelasan informasi.
      </InlineHint>

      <div className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="mb-4">
          <h2 className="font-manrope font-bold text-xl text-slate-900">
            Permintaan Pendaftaran Admin
          </h2>
          <p className="text-muted-foreground mt-1">
            Daftar admin baru yang menunggu persetujuan kepala ruangan
          </p>
        </div>

        {loadingSignupRequests ? (
          <div className="text-sm text-muted-foreground">Memuat permintaan...</div>
        ) : signupRequests.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            Tidak ada permintaan pendaftaran admin yang menunggu.
          </div>
        ) : (
          <div className="space-y-3">
            {signupRequests.map((request) => (
              <div
                key={request.request_id}
                className="flex flex-col gap-3 rounded-md border border-border/70 p-4 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <p className="font-manrope font-semibold text-foreground">{request.username}</p>
                  <p className="text-sm text-muted-foreground">{request.email}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Diajukan: {new Date(request.created_at).toLocaleString("id-ID")}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleApproveSignupRequest(request.request_id, request.username)}
                  >
                    <Check size={16} className="mr-2" />
                    Setujui
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleRejectSignupRequest(request.request_id, request.username)}
                  >
                    <X size={16} className="mr-2 text-destructive" />
                    Tolak
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.length === 0 ? (
          <div className="col-span-full">
            <PageEmptyState
              title="Belum ada pengguna"
              description="Tambahkan akun baru untuk mulai mengatur pembagian akses sesuai role yang tersedia."
            />
          </div>
        ) : (
          users.map((item) => (
            <div
              key={item.user_id}
              data-testid={`user-card-${item.user_id}`}
              className="rounded-lg border bg-card p-6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 text-primary p-3 rounded-full">
                    <Users size={20} />
                  </div>
                  <div>
                    <h3 className="font-manrope font-bold text-lg">
                      {item.username}
                    </h3>
                    <p className="text-sm text-muted-foreground">{item.email}</p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    item.role === ROLE_KEPALA_RUANGAN
                      ? "bg-primary/10 text-primary"
                      : item.role === ROLE_PENGAWAS
                        ? "bg-amber-100 text-amber-800"
                      : item.role === ROLE_USER
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {item.role}
                </span>
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid={`edit-user-${item.user_id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(item)}
                  className="flex-1"
                >
                  <Edit size={16} className="mr-2" />
                  Edit
                </Button>
                <Button
                  data-testid={`delete-user-${item.user_id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(item.user_id)}
                >
                  <Trash2 size={16} className="text-destructive" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Edit Pengguna" : "Tambah Pengguna"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <InlineHint>
              Perubahan di sini hanya memengaruhi data akun. Hak akses tetap mengikuti role yang dipilih.
            </InlineHint>

            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                data-testid="form-username"
                value={formData.username}
                onChange={(e) =>
                  {
                    setFormData({ ...formData, username: e.target.value });
                    setFormErrors((prev) => ({ ...prev, username: "" }));
                  }
                }
                className={getFieldErrorClass(formErrors.username)}
                aria-invalid={Boolean(formErrors.username)}
                required
                disabled={editMode}
              />
              {formErrors.username ? (
                <p className={getFieldMessageClass(true)}>{formErrors.username}</p>
              ) : (
                <p className={getFieldMessageClass()}>
                  {editMode ? "Username dikunci untuk menjaga konsistensi histori akun." : "Gunakan nama akun yang mudah dikenali tim."}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                data-testid="form-email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  {
                    setFormData({ ...formData, email: e.target.value });
                    setFormErrors((prev) => ({ ...prev, email: "" }));
                  }
                }
                className={getFieldErrorClass(formErrors.email)}
                aria-invalid={Boolean(formErrors.email)}
                required
              />
              {formErrors.email ? <p className={getFieldMessageClass(true)}>{formErrors.email}</p> : null}
            </div>

            <div className="space-y-2">
              <Label>Password {editMode && "(kosongkan jika tidak diubah)"}</Label>
              <Input
                data-testid="form-password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  {
                    setFormData({ ...formData, password: e.target.value });
                    setFormErrors((prev) => ({ ...prev, password: "" }));
                  }
                }
                className={getFieldErrorClass(formErrors.password)}
                aria-invalid={Boolean(formErrors.password)}
                required={!editMode}
              />
              {formErrors.password ? (
                <p className={getFieldMessageClass(true)}>{formErrors.password}</p>
              ) : (
                <p className={getFieldMessageClass()}>
                  {editMode ? "Isi hanya jika ingin mengganti password pengguna ini." : "Gunakan minimal 8 karakter agar akun lebih aman."}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) =>
                  {
                    setFormData({ ...formData, role: value });
                    setFormErrors((prev) => ({ ...prev, role: "" }));
                  }
                }
              >
                <SelectTrigger
                  data-testid="form-role"
                  className={getFieldErrorClass(formErrors.role)}
                  aria-invalid={Boolean(formErrors.role)}
                >
                  <SelectValue placeholder="Pilih role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ROLE_KEPALA_RUANGAN}>{ROLE_KEPALA_RUANGAN}</SelectItem>
                  <SelectItem value={ROLE_ADMIN}>{ROLE_ADMIN}</SelectItem>
                  <SelectItem value={ROLE_PENGAWAS}>{ROLE_PENGAWAS}</SelectItem>
                  <SelectItem value={ROLE_USER}>{ROLE_USER}</SelectItem>
                </SelectContent>
              </Select>
              {formErrors.role ? (
                <p className={getFieldMessageClass(true)}>{formErrors.role}</p>
              ) : (
                <p className={getFieldMessageClass()}>
                  Role menentukan modul yang bisa diakses tanpa mengubah alur bisnis yang ada.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Batal
              </Button>
              <Button data-testid="submit-user-button" type="submit">
                {editMode ? "Update" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmActionDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, open, onConfirm: open ? prev.onConfirm : null }))
        }
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        destructive={confirmDialog.destructive}
        onConfirm={async () => {
          if (confirmDialog.onConfirm) {
            await confirmDialog.onConfirm();
          }
          setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
        }}
      />
    </PageShell>
  );
};

export default UserManagementPage;
